import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query, doc, updateDoc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useLang } from "../../lib/useLang";
import { isBackendConfigured } from "../../lib/catalog";
import { demoStore } from "../../lib/demoStore";
import { formatKWD } from "../../lib/format";
import { modifierSummary } from "../../data/modifiers";
import { formatSchedule } from "../../lib/schedule";
import type { Order, OrderStatus } from "../../lib/types";

const FLOW: OrderStatus[] = [
  "new",
  "confirmed",
  "preparing",
  "out_for_delivery",
  "delivered",
];

export default function OrdersBoard() {
  const { t, lang, pick } = useLang();
  const [orders, setOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (isBackendConfigured) {
      const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
      return onSnapshot(q, (snap) =>
        setOrders(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Order, "id">) }))),
      );
    }
    // Demo: poll the in-memory store.
    setOrders(demoStore.getOrders());
    const iv = setInterval(() => setOrders(demoStore.getOrders()), 2000);
    return () => clearInterval(iv);
  }, []);

  async function advance(o: Order) {
    const idx = FLOW.indexOf(o.orderStatus);
    const next = FLOW[Math.min(idx + 1, FLOW.length - 1)];
    if (isBackendConfigured) {
      await updateDoc(doc(db, "orders", o.id), { orderStatus: next });
    } else {
      demoStore.setOrderStatus(o.id, { orderStatus: next });
      setOrders(demoStore.getOrders());
    }
  }

  if (orders.length === 0) {
    return <p className="card p-6 text-center text-black/50">{t("admin.ordersEmpty")}</p>;
  }

  return (
    <div className="space-y-3">
      {orders.map((o) => (
        <div key={o.id} className="card p-4">
          <div className="flex items-center justify-between">
            <span className="font-extrabold text-brand" dir="ltr">
              #{o.id}
            </span>
            <span className="chip bg-brand/10 text-brand">
              {t(`admin.status.${o.orderStatus}`)}
            </span>
          </div>
          <div className="mt-1 text-sm font-semibold text-gray-800">
            {o.customer.name} · <span dir="ltr">{o.customer.phone}</span>
          </div>
          {o.scheduledFor != null && (
            <div className="mt-1 inline-block rounded-lg bg-brand/10 px-2 py-0.5 text-xs font-bold text-brand">
              🗓️ {formatSchedule(o.scheduledFor, lang)}
            </div>
          )}
          <div className="text-xs text-black/55">
            {o.fulfillmentType === "pickup" ? (
              <span className="font-semibold text-accent-dark">🏪 {t("location.pickup")}</span>
            ) : (
              <>
                {o.address.area} · {t("checkout.block")} {o.address.block} ·{" "}
                {t("checkout.street")} {o.address.street} · {o.address.building}
              </>
            )}
          </div>

          <ul className="mt-2 space-y-0.5 text-sm">
            {o.lines.map((l, i) => {
              const mods = modifierSummary(l.modifiers, lang);
              return (
                <li key={l.uid ?? `${l.itemId}-${i}`}>
                  <div className="flex justify-between">
                    <span>
                      {l.qty}× {pick(l.name, l.nameAr)}
                    </span>
                    <span className="text-black/50">{formatKWD(l.price * l.qty, lang)}</span>
                  </div>
                  {mods && (
                    <div className="text-xs font-semibold text-ink">🍽️ {mods}</div>
                  )}
                  {l.notes && (
                    <div className="text-xs font-medium text-brand">📝 {l.notes}</div>
                  )}
                </li>
              );
            })}
          </ul>

          <div className="mt-2 flex items-center justify-between border-t border-black/5 pt-2">
            <span className="text-sm">
              <span className="chip bg-black/5 text-black/60">
                {t(`checkout.${o.paymentMethod}`)}
              </span>{" "}
              <span className="chip bg-gold/20 text-amber-700">
                {t(`admin.pay.${o.paymentStatus}`)}
              </span>
            </span>
            <span className="font-bold">{formatKWD(o.total || o.subtotal, lang)}</span>
          </div>

          {o.orderStatus !== "delivered" && o.orderStatus !== "cancelled" && (
            <button className="btn-primary mt-3 w-full text-sm" onClick={() => advance(o)}>
              → {t(`admin.status.${FLOW[Math.min(FLOW.indexOf(o.orderStatus) + 1, FLOW.length - 1)]}`)}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
